import { Button, Dialog, DialogActions, DialogContent } from '@material-ui/core';
import { DialogContentText, DialogTitle, FormControlLabel, FormLabel } from '@material-ui/core';
import { Radio, RadioGroup, Snackbar, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FunctionComponent, useEffect, useState } from 'react';

import { useAppDispatch } from '../../../hooks';
import {
    clearReportBugError,
    clearReportBugSuccess,
    GeneralSelectors,
    sendBugReport
} from '../../../reducers/general';
import WorkDialog from '../../../shared/dialog';

const useStyles = makeStyles((theme) => ({
    root: {
        width: 'calc(100% - 10px)',
        flexDirection: 'column',
        display: 'flex',
        justifyContent: 'center'
    },
    feedbackDialogTitle: {
        color: theme.palette.text.secondary
    },
    feedbackDialogRadio: {
        color: theme.palette.text.secondary
    },
    feedbackDialogInput: {
        color: theme.palette.text.secondary
    }
}));

type Props = {
    workId: number;
    feedbackDialogOpen: boolean;
    cancellationDialogOpen: boolean;
    inactivityPingDialogOpen: boolean;
    inactivityPingNumber: number;
    onSubmitFeedback: (feedback: string) => void;
    onConfirmCancel: () => void;
    onCloseFeedbackDialog: () => void;
    onCloseCancellationDialog: () => void;
    onCloseInactivityPingDialog: () => void;
};

const Dialogs: FunctionComponent<Props> = ({
    workId,
    feedbackDialogOpen,
    cancellationDialogOpen,
    inactivityPingDialogOpen,
    inactivityPingNumber,
    onSubmitFeedback,
    onConfirmCancel,
    onCloseFeedbackDialog,
    onCloseCancellationDialog,
    onCloseInactivityPingDialog,
    ...other
}: Props) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();

    const [ feedback, setFeedback ] = useState('');
    const [ reportType, setReportType ] = useState('');
    const { isReportBugError, isReportBugSuccess } = GeneralSelectors();

    const closeReportDialog = () => {
        setFeedback('');
        onCloseFeedbackDialog();
    };

    const handleRadioChange = (e) => {
        setReportType(e.target.value);
    };

    const onSubmitFeedbackClick = () => {
        if (feedback.trim() && reportType) {
            if (reportType === 'task') {
                onSubmitFeedback(feedback);
            } else if (reportType === 'general') {
                dispatch(sendBugReport({ workId, details: feedback }));
            }
            setFeedback('');
        }
    };

    useEffect(() => {
        if (isReportBugSuccess && feedbackDialogOpen) {
            closeReportDialog();
        }
    }, [ isReportBugSuccess, feedbackDialogOpen ]);

    return (
        <div
            className={classes.root}
            {...other}
        >

            {/* report dialog — custom layout, not suitable for WorkDialog */}
            <Dialog
                open={feedbackDialogOpen}
                onClose={closeReportDialog}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle id="form-dialog-title" className={classes.feedbackDialogTitle}>
                    Feedback
                </DialogTitle>
                <DialogContent>
                    <FormLabel component="legend">
                        What is your feedback or question about?
                    </FormLabel>
                    <RadioGroup
                        aria-label="reportType"
                        name="reportType"
                        value={reportType}
                        onChange={handleRadioChange}
                    >
                        <FormControlLabel
                            className={classes.feedbackDialogRadio}
                            value="task"
                            control={<Radio />}
                            label="This specific task"
                        />
                        <FormControlLabel
                            className={classes.feedbackDialogRadio}
                            value="general"
                            control={<Radio />}
                            label="The general platform"
                        />
                    </RadioGroup>
                    <DialogContentText>
                        Please be as detailed as you can about your feedback or question.
                        We appreciate your help!
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Description"
                        type="description"
                        fullWidth
                        multiline
                        rowsMax={8}
                        helperText={reportType === 'task' ? 'After sending your feedback/question, the task will not be available until your feedback is addressed.' : ''}
                        onChange={e => setFeedback(e.target.value)}
                        InputProps={{
                            classes: {
                                root: classes.feedbackDialogInput
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReportDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={onSubmitFeedbackClick} color="secondary">
                        Send
                    </Button>
                </DialogActions>
            </Dialog>

            <WorkDialog
                open={cancellationDialogOpen}
                onClose={onCloseCancellationDialog}
                title="Cancel Task"
                description={[
                    'Are you sure you want to cancel the task?',
                    'If you made any progress on this task, please push your changes before canceling the task.'
                ]}
                buttons={[
                    { label: 'Don\'t Cancel', onClick: onCloseCancellationDialog },
                    { label: 'Cancel', onClick: onConfirmCancel }
                ]}
            />

            <WorkDialog
                open={inactivityPingDialogOpen}
                onClose={onCloseInactivityPingDialog}
                title={`Inactivity Warning (${inactivityPingNumber} of 2)`}
                description={[
                    'No activity has been detected for a while. Are you still working on this task?',
                    'Click "I\'m Still Working" to continue, or the task will be automatically cancelled.'
                ]}
                buttons={[
                    { label: 'Close', onClick: onCloseInactivityPingDialog }
                ]}
            />

            <Snackbar
                open={isReportBugError}
                autoHideDuration={4000}
                onClose={() => dispatch(clearReportBugError())}
                message="Woops! Not sure what happened there... Please try again"
            />
            <Snackbar
                open={isReportBugSuccess}
                autoHideDuration={4000}
                onClose={() => dispatch(clearReportBugSuccess())}
                message="The bug report has been submitted successfully. Thank you!"
            />
        </div>
    );
};

export default Dialogs;
